import type { FC } from 'react'
import { RadioGroup } from '@headlessui/react'
import type { BigNumber } from '@ethersproject/bignumber'
import type { ERC20Names, ERC20Symbols } from '@g4g/wallet-modal/types'
import CheckoutPaymentTokenSquare from './CheckoutPaymentTokenSquare'
import { classNames } from '@g4g/utils/src/react/class-names'
import { useUpdateEffect } from 'react-use'
import useHodlPrpsBurnCalculator from '../../../hooks/useHodlPrpsBurnCalculator'
import { useCheckoutOpsDispatch } from '../../../CheckoutProvider'

interface CheckoutTokenAmount {
  wei?: BigNumber
  human?: string
}

interface CheckoutToken {
  totalBalance?: CheckoutTokenAmount
  locked?: CheckoutTokenAmount
  price?: {
    wei?: string
    human?: string
  }
}

const CheckoutPaymentTokenRadioOpt: FC<{
  name: ERC20Names
  symbol: ERC20Symbols
  loading: boolean
  token: CheckoutToken
  insufficientFunds: boolean
}> = ({ name, loading, symbol, token, insufficientFunds }) => {
  const disabled = loading || insufficientFunds
  const dispatch = useCheckoutOpsDispatch()
  const { amount } = useHodlPrpsBurnCalculator(
    token.totalBalance?.wei,
    token.locked?.wei,
    token.price?.wei
  )

  useUpdateEffect(() => {
    // Locked prps necessary to burn to complete the payment
    if (name === 'Purpose') {
      dispatch({ type: 'update-locked-prps', lockedPrps: amount })
    }
  }, [amount, dispatch, name])

  return (
    <RadioGroup.Option
      data-cy="shop-checkout-token-picker-option"
      disabled={disabled}
      className={classNames(
        'w-1/2',
        disabled ? 'opacity-50 cursor-default select-none' : 'cursor-pointer'
      )}
      value={name}
    >
      {({ checked }: { checked: boolean }) => (
        <CheckoutPaymentTokenSquare
          loading={loading}
          disabled={disabled}
          tokenSymbol={symbol}
          checked={checked}
          humanPrice={token.price?.human}
          humanBalance={token.totalBalance?.human}
        />
      )}
    </RadioGroup.Option>
  )
}

export default CheckoutPaymentTokenRadioOpt
